// ✅ ALTERED ONLY FOR THEME (matches the “above theme”)
// - Orange header bar + white title
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Inputs: soft blue border + orange focus ring
// - Buttons: primary orange, outlined blue
// - Table/cards: blue header, soft zebra rows + hover tint
// - Added AcademicYear dropdown
// - Added Existing Lessons table by SubjectCode
// - Added Create from Existing Lesson flow
// - Added JSON upload support

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Collapse,
  Checkbox,
  Dialog,
} from "@mui/material";
import Slider from "@mui/material/Slider";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import UploadIcon from "@mui/icons-material/Upload";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import PublishIcon from "@mui/icons-material/Publish";
import PreviewIcon from "@mui/icons-material/Preview";
import DescriptionIcon from "@mui/icons-material/Description";
import DataObjectIcon from "@mui/icons-material/DataObject";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HistoryIcon from "@mui/icons-material/History";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import { API_URL } from "./config";

const AiLessonBuilderPage = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "#1565C0";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    borderRadius: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const filterSectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const sectionTitleSx = {
    fontWeight: 900,
    color: BLUE,
    mb: 1,
  };

  const [quizQuestionCount, setQuizQuestionCount] = useState(15);
  const [quizQuestionType, setQuizQuestionType] = useState("MCQ");
  const assessmentInstructions = `Create exactly ${quizQuestionCount} final review questions. Question format: ${quizQuestionType}. The class is the selected ClassID. The lesson must use UniFLOO's assessment protocol; grading and answer security are handled by the backend. Do not embed answer keys in student-visible JavaScript.`;
  const [previewInteractiveState, setPreviewInteractiveState] = useState({});
  const setPreviewInteractiveValue = (key, value) => {
    setPreviewInteractiveState((prev) => ({ ...prev, [key]: value }));
  };

  const getAccentStyles = (accent) => {
    switch (accent) {
      case "blue":
        return {
          borderColor: "#4FC3F7",
          color: "#1E88E5",
          backgroundColor: "rgba(79,195,247,0.08)",
        };
      case "purple":
        return {
          borderColor: "#B39DDB",
          color: "#7E57C2",
          backgroundColor: "rgba(179,157,219,0.08)",
        };
      case "pink":
        return {
          borderColor: "#F48FB1",
          color: "#D81B60",
          backgroundColor: "rgba(244,143,177,0.08)",
        };
      case "green":
        return {
          borderColor: "#81C784",
          color: "#2E7D32",
          backgroundColor: "rgba(129,199,132,0.08)",
        };
      case "orange":
        return {
          borderColor: "#FFCC80",
          color: "#EF6C00",
          backgroundColor: "rgba(255,204,128,0.10)",
        };
      default:
        return {
          borderColor: BORDER,
          color: BLUE,
          backgroundColor: "rgba(25,118,210,0.03)",
        };
    }
  };

  const classifyAngle = (value, bands = []) => {
    const n = Number(value || 0);
    const band = bands.find((b) => n >= Number(b.min) && n <= Number(b.max));
    return band?.label || `${n}°`;
  };

  const computeMissingAngle = (cfg) => {
    const total = Number(cfg?.total || 180);
    const known = Array.isArray(cfg?.knownAngles) ? cfg.knownAngles.map(Number) : [];
    const sum = known.reduce((a, b) => a + b, 0);
    return total - sum;
  };

  const [academicYearOptions, setAcademicYearOptions] = useState([]);

  const [lessonFilters, setLessonFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Semester: [],
    Section: [],
    SubjectCombo: [],
    Status: [],
  });

  const [lessonFilterOptions, setLessonFilterOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Semester: [],
    Section: [],
    SubjectCombo: [],
    Status: [],
  });

  const [existingLessonsRaw, setExistingLessonsRaw] = useState([]);
  const [existingLessonsFiltered, setExistingLessonsFiltered] = useState([]);
  const [expandedSubjectGroups, setExpandedSubjectGroups] = useState({});
  const [lessonPage, setLessonPage] = useState(1);
  const [selectedLessonIds, setSelectedLessonIds] = useState([]);
  const [bulkPublishing, setBulkPublishing] = useState(false);

  const LESSONS_PER_PAGE = 20;

  const renderInteractivePreviewSection = (sec, idx) => {
    const sectionKey = sec.sectionKey || `sec_${idx}`;

    if (sec.interactionType === "INFO_CARDS") {
      const cards = Array.isArray(sec.cards) ? sec.cards : [];
      return (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {cards.map((card, cIdx) => {
            const accent = getAccentStyles(card.accent);
            return (
              <Grid item xs={12} sm={6} md={4} key={cIdx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: `2px solid ${accent.borderColor}`,
                    backgroundColor: accent.backgroundColor,
                    minHeight: 150,
                  }}
                >
                  <Typography sx={{ fontWeight: 900, color: accent.color, mb: 1 }}>
                    {card.title}
                  </Typography>
                  <Typography sx={{ mb: 1 }}>{card.subtitle}</Typography>
                  {Array.isArray(card.meta) && card.meta.length > 0 && (
                    <Stack spacing={0.5}>
                      {card.meta.map((m, i) => (
                        <Typography key={i} sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
                          {m}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      );
    }

    if (sec.interactionType === "ANGLE_SLIDER") {
      const cfg = sec.sliderConfig || {};
      const value = previewInteractiveState[sectionKey] ?? Number(cfg.initialValue ?? cfg.min ?? 0);

      return (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 2,
            borderRadius: 3,
            border: "2px dashed #66BB6A",
            backgroundColor: "rgba(102,187,106,0.06)",
          }}
        >
          <Typography sx={{ fontWeight: 900, color: "#43A047", mb: 1 }}>
            🎮 {sec.title}
          </Typography>

          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            {sec.explanation || "Move the slider to explore the angle."}
          </Typography>

          <Box sx={{ px: 1, mb: 2 }}>
            <Slider
              value={value}
              min={Number(cfg.min ?? 0)}
              max={Number(cfg.max ?? 180)}
              step={1}
              onChange={(_, v) => setPreviewInteractiveValue(sectionKey, v)}
            />
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              textAlign: "center",
              border: "2px solid #4FC3F7",
              backgroundColor: "rgba(79,195,247,0.08)",
            }}
          >
            <Typography sx={{ fontSize: "1.8rem", fontWeight: 900, color: "#2E7D32" }}>
              {value}°
            </Typography>
            <Typography sx={{ fontWeight: 800, color: THEME_ORANGE }}>
              {classifyAngle(value, cfg.bands || [])}
            </Typography>
          </Paper>
        </Paper>
      );
    }

    if (sec.interactionType === "MISSING_ANGLE") {
      const cfg = sec.missingAngleConfig || {};
      const result = computeMissingAngle(cfg);

      return (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 2,
            borderRadius: 3,
            border: "2px dashed #66BB6A",
            backgroundColor: "rgba(102,187,106,0.06)",
          }}
        >
          <Typography sx={{ fontWeight: 900, color: "#43A047", mb: 1 }}>
            🎮 {sec.title}
          </Typography>

          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            {sec.explanation}
          </Typography>

          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
            {(cfg.knownAngles || []).map((a, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${BORDER}`,
                  backgroundColor: "white",
                  minWidth: 120,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                  Angle {i + 1}
                </Typography>
                <Typography sx={{ fontWeight: 900, color: "#2E7D32", fontSize: "1.4rem" }}>
                  {a}°
                </Typography>
              </Paper>
            ))}

            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1.25,
                borderRadius: 2,
                border: "2px solid #66BB6A",
                backgroundColor: "white",
                minWidth: 150,
                textAlign: "center",
              }}
            >
              <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                {cfg.resultLabel || "Missing Angle"}
              </Typography>
              <Typography sx={{ fontWeight: 900, color: "#2E7D32", fontSize: "1.6rem" }}>
                {result}
                {cfg.resultUnit || "°"}
              </Typography>
            </Paper>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: "1px solid #FFCC80",
              backgroundColor: "rgba(255,204,128,0.10)",
            }}
          >
            <Typography sx={{ color: "#8D6E63", fontWeight: 700 }}>
              {cfg.total || 180}° - {(cfg.knownAngles || []).join("° - ")}° = {result}°
            </Typography>
          </Paper>
        </Paper>
      );
    }

    if (sec.interactionType === "MCQ_GRID") {
      return (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {(sec.practice || []).map((pq, pIdx) => (
            <Paper
              key={pq.questionId || pIdx}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${BORDER}`,
                backgroundColor: "rgba(25,118,210,0.02)",
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 1 }}>
                {pIdx + 1}. {pq.questionText}
              </Typography>
              <Grid container spacing={1}>
                {(pq.options || []).map((opt, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${BORDER}`,
                        backgroundColor: "white",
                      }}
                    >
                      <Typography>{opt}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ))}
        </Stack>
      );
    }

    return null;
  };

  const fileInputRef = useRef(null);
  const token = localStorage.getItem("auth") || localStorage.getItem("token");

  const [campusCode, setCampusCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reusing, setReusing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tab, setTab] = useState(0);

  const [form, setForm] = useState({
    SubCampusCode: "",
    DegreeCode: "",
    BranchCode: "",
    Semester: "",
    Section: "",
    ClassID: "",
    SubjectCode: "",
    SubjectName: "",
    TextbookName: "",
    ChapterTitle: "",
    ChapterNumber: "",
    AcademicYearLabel: "",
    LanguageCode: "en",
    ScanMetadata: 1,
    GenerateAssignment: 1,
    PublishMode: "PUBLISHED",
  });

  const [subCampusOptions, setSubCampusOptions] = useState([]);
  const [degreeOptions, setDegreeOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [semesterOptions, setSemesterOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [selectedFileKind, setSelectedFileKind] = useState("");
  const [jsonPreview, setJsonPreview] = useState(null);
  const [jsonValidationMessage, setJsonValidationMessage] = useState("");

  const [selectedExistingLessonId, setSelectedExistingLessonId] = useState("");
  const [sourceExistingLessonId, setSourceExistingLessonId] = useState("");

  const [uploadResult, setUploadResult] = useState(null);
  const [generatedLesson, setGeneratedLesson] = useState(null);
  // Fetch HTML with the existing JWT; browser iframe/navigation requests cannot attach Axios headers.
  const [lessonPreviewHtml, setLessonPreviewHtml] = useState("");
  const [lessonPreviewLoading, setLessonPreviewLoading] = useState(false);
  const [lessonPreviewError, setLessonPreviewError] = useState("");
  const lessonPreviewId = generatedLesson?.LessonId;

  useEffect(() => {
    if (tab !== 4 || !lessonPreviewId || !token) {
      setLessonPreviewHtml("");
      setLessonPreviewError("");
      return;
    }
    let cancelled = false;
    setLessonPreviewLoading(true);
    setLessonPreviewError("");
    setLessonPreviewHtml("");
    axios.get(`${API_URL}/api/ai-lessons/${lessonPreviewId}/html`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "text",
    }).then((response) => {
      if (!cancelled) setLessonPreviewHtml(String(response.data || ""));
    }).catch((err) => {
      if (!cancelled) setLessonPreviewError(
        err.response?.data?.error || err.response?.data?.message ||
        `Unable to load lesson HTML (${err.response?.status || "network error"})`
      );
    }).finally(() => { if (!cancelled) setLessonPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [tab, lessonPreviewId, token]);

  const openLessonInNewTab = () => {
    if (!lessonPreviewHtml) return;
    // The generated lesson runs only inside an opaque-origin sandboxed frame.
    const escapedHtml = lessonPreviewHtml.replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const viewer = `<!doctype html><html><head><meta charset="utf-8"><title>Lesson Preview</title>` +
      `<style>html,body{margin:0;height:100%;background:#f2f2f2}iframe{width:100%;height:100%;border:0}</style>` +
      `</head><body><iframe title="Lesson Preview" sandbox="allow-scripts" referrerpolicy="no-referrer" srcdoc="${escapedHtml}"></iframe></body></html>`;
    const url = URL.createObjectURL(new Blob([viewer], { type: "text/html" }));
    const newTab = window.open(url, "_blank", "noopener,noreferrer");
    if (!newTab) setError("Please allow popups to open the lesson in a new tab.");
    // Delay revocation so Safari has time to load the blob document.
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const [lessonJsonText, setLessonJsonText] = useState("");

  const EMPTY_PROMPT = {
    PromptID: null,
    PromptType: "LESSON",
    PromptCategory: "LESSON_BUILDER",
    PromptTitle: "",
    PromptPurpose: "",
    PromptText: "",
    SubCampusCode: null,
  };
  const LESSON_PROMPT_CATEGORY = "LESSON_BUILDER";
  const [prompts, setPrompts] = useState([]);
  const [promptUsed, setPromptUsed] = useState("");
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptForm, setPromptForm] = useState(EMPTY_PROMPT);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);

  const openPromptDialog = () => {
    setPromptDialogOpen(true);
  };

  const closePromptDialog = () => {
    setPromptDialogOpen(false);
  };


  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const norm = (v) => String(v ?? "").trim().toLowerCase();

  const inFilter = (selected = [], value) =>
    !selected?.length || selected.some((s) => norm(s) === norm(value));

  const buildSubjectCombo = (row) =>
    `${row.SubjectCode || ""} - ${row.SubjectName || ""}`.trim();

  const groupKeyForLesson = (row) =>
    `${row.SubjectCode || ""}|||${row.SubjectName || ""}`;

  const toggleSubjectGroup = (groupKey) => {
    setExpandedSubjectGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const handleApplyLessonFilters = () => {
    setLessonPage(1);
    let filtered = [...existingLessonsRaw];

    filtered = filtered.filter((r) => {
      const subjectCombo = buildSubjectCombo(r);

      return (
        inFilter(lessonFilters.SubCampusCode, r.SubCampusCode) &&
        inFilter(lessonFilters.DegreeCode, r.DegreeCode) &&
        inFilter(lessonFilters.BranchCode, r.BranchCode) &&
        inFilter(lessonFilters.Semester, String(r.Semester ?? "")) &&
        inFilter(lessonFilters.Section, String(r.Section ?? "")) &&
        inFilter(lessonFilters.SubjectCombo, subjectCombo) &&
        inFilter(lessonFilters.Status, String(r.ReviewStatus ?? ""))
      );
    });

    setExistingLessonsFiltered(filtered);
  };

  const handleClearLessonFilters = () => {
    setLessonFilters({
      SubCampusCode: [],
      DegreeCode: [],
      BranchCode: [],
      Semester: [],
      Section: [],
      SubjectCombo: [],
      Status: [],
    });
    setLessonPage(1);
    setExistingLessonsFiltered(existingLessonsRaw);
  };

  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      const cc = payload?.campusCode || payload?.CampusCode || "";
      setCampusCode(cc);
    } catch {
      setError("Failed to decode auth token");
    }
  }, [token]);

  const fetchAcademicYears = async () => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/academicyears`, {
      headers: authHeaders,
    });
  
    const rows = Array.isArray(res.data) ? res.data : [];
    const values = rows
      .map((x) => x.AcademicYearLabel || "")
      .filter(Boolean);
  
    setAcademicYearOptions(values);
  };

  const fetchSubCampuses = async () => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/subcampuses`, {
      headers: authHeaders,
    });
    setSubCampusOptions(res.data || []);
  };

  const fetchDegrees = async (SubCampusCode) => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/degrees`, {
      headers: authHeaders,
      params: { SubCampusCode },
    });
    setDegreeOptions(res.data || []);
  };

  const fetchBranches = async (SubCampusCode, DegreeCode) => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/branches`, {
      headers: authHeaders,
      params: { SubCampusCode, DegreeCode },
    });
    setBranchOptions(res.data || []);
  };

  const fetchSemesters = async (SubCampusCode, DegreeCode, BranchCode) => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/semesters`, {
      headers: authHeaders,
      params: { SubCampusCode, DegreeCode, BranchCode },
    });
    setSemesterOptions(res.data || []);
  };

  const fetchSections = async (SubCampusCode, DegreeCode, BranchCode, Semester) => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/sections`, {
      headers: authHeaders,
      params: { SubCampusCode, DegreeCode, BranchCode, Semester },
    });
    setSectionOptions(res.data || []);
  };

  const fetchClasses = async (
    AcademicYearLabel,
    SubCampusCode,
    DegreeCode,
    BranchCode,
    Semester,
    Section
  ) => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/classes`, {
      headers: authHeaders,
      params: {
        AcademicYearLabel,
        SubCampusCode,
        DegreeCode,
        BranchCode,
        Semester,
        Section,
      },
    });
    setClassOptions(res.data || []);
  };

  const fetchClassDetails = async (ClassID) => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/class-details`, {
      headers: authHeaders,
      params: { ClassID },
    });

    const row = res.data || {};
    setForm((prev) => ({
      ...prev,
      ClassID: row.ClassID || "",
      SubCampusCode: row.SubCampusCode || prev.SubCampusCode,
      DegreeCode: row.DegreeCode || prev.DegreeCode,
      BranchCode: row.BranchCode || prev.BranchCode,
      Semester: row.Semester || prev.Semester,
      Section: row.Section || prev.Section,
      SubjectCode: row.SubjectCode || "",
      SubjectName: row.SubjectName || "",
    }));
  };

  const loadPrompts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-lessons/builder/prompts`, {
        headers: authHeaders,
        params: {
          PromptType: "LESSON",
          PromptCategory: LESSON_PROMPT_CATEGORY,
          SubCampusCode: form.SubCampusCode || undefined,
        },
      });
      setPrompts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setPrompts([]);
      setError(err.response?.data?.error || "Failed to load lesson prompts");
    }
  };

  const savePrompt = async () => {
    if (!String(promptForm.PromptTitle || "").trim() || !String(promptForm.PromptText || "").trim()) {
      setError("Prompt title and prompt text are required");
      return;
    }
    setSavingPrompt(true);
    setError("");
    try {
      const payload = {
        ...promptForm,
        PromptType: "LESSON",
        PromptCategory: promptForm.PromptCategory || LESSON_PROMPT_CATEGORY,
        SubCampusCode: promptForm.SubCampusCode || null,
      };
      if (promptForm.PromptID) {
        await axios.put(`${API_URL}/api/ai-lessons/builder/prompts/${promptForm.PromptID}`, payload, { headers: authHeaders });
      } else {
        await axios.post(`${API_URL}/api/ai-lessons/builder/prompts`, payload, { headers: authHeaders });
      }
      setPromptForm(EMPTY_PROMPT);
      await loadPrompts();
      setSuccess(promptForm.PromptID ? "Prompt updated" : "Prompt saved");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save prompt");
    } finally {
      setSavingPrompt(false);
    }
  };

  const deletePrompt = async (promptId) => {
    try {
      await axios.delete(`${API_URL}/api/ai-lessons/builder/prompts/${promptId}`, { headers: authHeaders });
      if (selectedPrompt?.PromptID === promptId) {
        setSelectedPrompt(null);
        setPromptUsed("");
      }
      await loadPrompts();
      setSuccess("Prompt deleted");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete prompt");
    }
  };

  const fetchExistingLessons = async (overrideParams = {}) => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-lessons/builder/existing-lessons`, {
        headers: authHeaders,
        params: {
          SubCampusCode: overrideParams.SubCampusCode ?? form.SubCampusCode ?? undefined,
          DegreeCode: overrideParams.DegreeCode ?? form.DegreeCode ?? undefined,
          BranchCode: overrideParams.BranchCode ?? form.BranchCode ?? undefined,
          Semester: overrideParams.Semester ?? form.Semester ?? undefined,
          Section: overrideParams.Section ?? form.Section ?? undefined,
          SubjectCode: overrideParams.SubjectCode ?? undefined, // do not default to form.SubjectCode
          ClassID: overrideParams.ClassID ?? undefined,         // do not default to form.ClassID
        },
      });
  
      const rows = Array.isArray(res.data) ? res.data : [];
      setExistingLessonsRaw(rows);
      setExistingLessonsFiltered(rows);
  
      const uniq = (arr) =>
        Array.from(new Set(arr.filter((x) => String(x ?? "").trim() !== ""))).sort((a, b) =>
          String(a).localeCompare(String(b))
        );
  
      setLessonFilterOptions({
        SubCampusCode: uniq(rows.map((r) => r.SubCampusCode)),
        DegreeCode: uniq(rows.map((r) => r.DegreeCode)),
        BranchCode: uniq(rows.map((r) => r.BranchCode)),
        Semester: uniq(rows.map((r) => String(r.Semester ?? ""))),
        Section: uniq(rows.map((r) => String(r.Section ?? ""))),
        SubjectCombo: uniq(rows.map((r) => `${r.SubjectCode || ""} - ${r.SubjectName || ""}`.trim())),
        Status: uniq(rows.map((r) => String(r.ReviewStatus || ""))),
      });
  
      const initialExpanded = {};
      rows.forEach((r) => {
        const key = `${r.SubjectCode || ""}|||${r.SubjectName || ""}`;
        if (!(key in initialExpanded)) initialExpanded[key] = false;
      });
      setExpandedSubjectGroups(initialExpanded);
      setSelectedLessonIds([]);
      setLessonPage(1);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load existing lessons");
    }
  };

  const getExistingLessonDetail = async (lessonId) => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/existing-lessons/${lessonId}`, {
      headers: authHeaders,
    });
    return res.data || {};
  };

  const handleUseExistingLesson = async (lessonId) => {
    setLoading(true);
    setError("");
    try {
      const row = await getExistingLessonDetail(lessonId);
      const lessonJson = row.LessonJson || {};
      const meta = row.LessonMetaJson || {};

      setSelectedExistingLessonId(row.LessonId || lessonId);
      setSourceExistingLessonId(row.LessonId || lessonId);
      setSelectedFile(null);
      setPreviewName(row.LessonTitle ? `Existing: ${row.LessonTitle}` : `Existing Lesson #${row.LessonId || lessonId}`);
      setSelectedFileKind("EXISTING");
      setJsonPreview(null);
      setJsonValidationMessage("");

      setForm((prev) => ({
        ...prev,
        AcademicYearLabel: row.AcademicYearLabel || prev.AcademicYearLabel,
        SubCampusCode: row.SubCampusCode || "",
        DegreeCode: row.DegreeCode || "",
        BranchCode: row.BranchCode || "",
        Semester: row.Semester ?? "",
        Section: row.Section || "",
        ClassID: row.ClassID || "",
        SubjectCode: row.SubjectCode || "",
        SubjectName: row.SubjectName || "",
        TextbookName: row.TextbookName || "",
        ChapterTitle: row.ChapterTitle || "",
        ChapterNumber: row.ChapterNumber || "",
        LanguageCode: row.LanguageCode || prev.LanguageCode || "en",
        PublishMode: "DRAFT",
      }));

      setLessonJsonText(JSON.stringify(lessonJson, null, 2));
      setGeneratedLesson(null);
      setUploadResult(null);

      const metaPromptId = Number(meta?.promptId || meta?.PromptID || 0) || null;
      const metaPromptText = meta?.promptUsed || meta?.PromptUsed || "";
      const matchedPrompt = metaPromptId
        ? prompts.find((p) => Number(p.PromptID) === metaPromptId) || null
        : null;

      setSelectedPrompt(matchedPrompt);
      setPromptUsed(matchedPrompt?.PromptText || metaPromptText || "");

      setTab(0);
      setSuccess("Existing lesson loaded into Create Lesson. Review the scope, prompt and lesson settings, then generate the new lesson.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load existing lesson");
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingLessonDetail = async (lessonId) => {
    try {
      const row = await getExistingLessonDetail(lessonId);
      const lessonJson = row.LessonJson || {};
      setSelectedExistingLessonId(row.LessonId || "");
      setLessonJsonText(JSON.stringify(lessonJson, null, 2));
      setGeneratedLesson({
        LessonId: row.LessonId,
        LessonTitle: row.LessonTitle,
        SubjectCode: row.SubjectCode,
        SubjectName: row.SubjectName,
        ChapterTitle: row.ChapterTitle,
        AcademicYearLabel: row.AcademicYearLabel,
        ReviewStatus: row.ReviewStatus,
        LessonJson: lessonJson,
        RenderedHtml: row.RenderedHtml || null,
        SubCampusCode: row.SubCampusCode,
        DegreeCode: row.DegreeCode,
        BranchCode: row.BranchCode,
        Semester: row.Semester,
        Section: row.Section,
      });
      setTab(4);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load existing lesson");
    }
  };

  useEffect(() => {
    if (!token || !campusCode) return;
    fetchSubCampuses().catch(() => setError("Failed to load subcampuses"));
    fetchAcademicYears().catch(() => setError("Failed to load academic years"));
    loadPrompts();
  }, [token, campusCode]);

  useEffect(() => {
    if (!token || !campusCode) return;
    loadPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.SubCampusCode]);

  useEffect(() => {
    if (!form.SubCampusCode) {
      setDegreeOptions([]);
      return;
    }
    fetchDegrees(form.SubCampusCode).catch(() => setError("Failed to load degrees"));
  }, [form.SubCampusCode]);

  useEffect(() => {
    if (!form.SubCampusCode || !form.DegreeCode) {
      setBranchOptions([]);
      return;
    }
    fetchBranches(form.SubCampusCode, form.DegreeCode).catch(() => setError("Failed to load branches"));
  }, [form.SubCampusCode, form.DegreeCode]);

  useEffect(() => {
    if (!form.SubCampusCode || !form.DegreeCode || !form.BranchCode) {
      setSemesterOptions([]);
      return;
    }
    fetchSemesters(form.SubCampusCode, form.DegreeCode, form.BranchCode).catch(() =>
      setError("Failed to load semesters")
    );
  }, [form.SubCampusCode, form.DegreeCode, form.BranchCode]);

  useEffect(() => {
    if (!form.SubCampusCode || !form.DegreeCode || !form.BranchCode || !form.Semester) {
      setSectionOptions([]);
      return;
    }
    fetchSections(form.SubCampusCode, form.DegreeCode, form.BranchCode, form.Semester).catch(() =>
      setError("Failed to load sections")
    );
  }, [form.SubCampusCode, form.DegreeCode, form.BranchCode, form.Semester]);

  useEffect(() => {
    if (
      !form.AcademicYearLabel ||
      !form.SubCampusCode ||
      !form.DegreeCode ||
      !form.BranchCode ||
      !form.Semester ||
      !form.Section
    ) {
      setClassOptions([]);
      return;
    }

    fetchClasses(
      form.AcademicYearLabel,
      form.SubCampusCode,
      form.DegreeCode,
      form.BranchCode,
      form.Semester,
      form.Section
    ).catch(() => setError("Failed to load classes"));
  }, [
    form.AcademicYearLabel,
    form.SubCampusCode,
    form.DegreeCode,
    form.BranchCode,
    form.Semester,
    form.Section,
  ]);

  useEffect(() => {
    if (!form.ClassID) return;
    fetchClassDetails(form.ClassID).catch(() => setError("Failed to load class details"));
  }, [form.ClassID]);


  useEffect(() => {
    if (!token || !campusCode) return;
    fetchExistingLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, campusCode]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      if (name === "AcademicYearLabel") {
        return {
          ...prev,
          AcademicYearLabel: value,
          ClassID: "",
          SubjectCode: "",
          SubjectName: "",
        };
      }

      if (name === "SubCampusCode") {
        return {
          ...prev,
          SubCampusCode: value,
          DegreeCode: "",
          BranchCode: "",
          Semester: "",
          Section: "",
          ClassID: "",
          SubjectCode: "",
          SubjectName: "",
          TextbookName: "",
          ChapterTitle: "",
          ChapterNumber: "",
        };
      }

      if (name === "DegreeCode") {
        return {
          ...prev,
          DegreeCode: value,
          BranchCode: "",
          Semester: "",
          Section: "",
          ClassID: "",
          SubjectCode: "",
          SubjectName: "",
          TextbookName: "",
          ChapterTitle: "",
          ChapterNumber: "",
        };
      }

      if (name === "BranchCode") {
        return {
          ...prev,
          BranchCode: value,
          Semester: "",
          Section: "",
          ClassID: "",
          SubjectCode: "",
          SubjectName: "",
          TextbookName: "",
          ChapterTitle: "",
          ChapterNumber: "",
        };
      }

      if (name === "Semester") {
        return {
          ...prev,
          Semester: value,
          Section: "",
          ClassID: "",
          SubjectCode: "",
          SubjectName: "",
          TextbookName: "",
          ChapterTitle: "",
          ChapterNumber: "",
        };
      }

      if (name === "Section") {
        return {
          ...prev,
          Section: value,
          ClassID: "",
          SubjectCode: "",
          SubjectName: "",
          TextbookName: "",
          ChapterTitle: "",
          ChapterNumber: "",
        };
      }

      if (name === "ClassID") {
        return {
          ...prev,
          ClassID: value,
          SubjectCode: "",
          SubjectName: "",
          TextbookName: "",
          ChapterTitle: "",
          ChapterNumber: "",
        };
      }

      return { ...prev, [name]: value };
    });

    if (name === "ClassID") {
      //setExistingLessons([]);
      setSelectedExistingLessonId("");
      setGeneratedLesson(null);
      setLessonJsonText("");
      setUploadResult(null);
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const validateLessonJsonShape = (obj) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
      return "JSON root must be an object.";
    }

    const possibleTopLevelKeys = [
      "lessonTitle",
      "chapterTitle",
      "sections",
      "learningObjectives",
      "assignment",
      "LessonJson",
      "page",
      "pages",
    ];

    const hasAnyUsefulKey = possibleTopLevelKeys.some((k) => Object.prototype.hasOwnProperty.call(obj, k));
    if (!hasAnyUsefulKey) {
      return "JSON uploaded, but expected lesson/page-like keys were not found.";
    }

    return "";
  };

  const handleFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setJsonPreview(null);
    setJsonValidationMessage("");
    setSelectedFile(file);
    setSourceExistingLessonId("");
    setSelectedExistingLessonId("");
    setPreviewName(file.name);

    const lowerName = String(file.name || "").toLowerCase();
    const isJson = lowerName.endsWith(".json") || file.type === "application/json";

    if (isJson) {
      setSelectedFileKind("JSON");
      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        setJsonPreview(parsed);

        const validationMsg = validateLessonJsonShape(parsed);
        setJsonValidationMessage(validationMsg || "Valid JSON detected.");
        if (validationMsg) {
          setError(validationMsg);
        }
      } catch {
        setJsonPreview(null);
        setJsonValidationMessage("");
        setError("Selected JSON file is invalid.");
      }
      return;
    }

    const isPdf = file.type === "application/pdf" || lowerName.endsWith(".pdf");
    const isImage =
      ["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type) ||
      [".jpg", ".jpeg", ".png", ".webp"].some((ext) => lowerName.endsWith(ext));

    if (isPdf) setSelectedFileKind("PDF");
    else if (isImage) setSelectedFileKind("IMAGE");
    else setSelectedFileKind("FILE");
  };

  const acceptedTypesLabel = useMemo(
    () => "Accepted: PDF, JPG, JPEG, PNG, WEBP, JSON",
    []
  );

  const handleGenerate = async ({ previewAfter = false, successMessage = "Lesson generated successfully" } = {}) => {
    if (!selectedFile && !sourceExistingLessonId) {
      setError("Please upload a textbook PDF/image/JSON file, or choose a lesson from Create From Existing");
      return;
    }
    if (!form.ClassID) {
      setError("Please select a class");
      return;
    }
    if (!form.AcademicYearLabel) {
      setError("Please select Academic Year");
      return;
    }
    if (!sourceExistingLessonId && selectedFileKind === "JSON" && !jsonPreview) {
      setError("Please upload a valid JSON file");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setGeneratedLesson(null);
    setUploadResult(null);

    try {
      if (sourceExistingLessonId) {
        let parsedLessonJson = {};
        try {
          parsedLessonJson = JSON.parse(lessonJsonText || "{}");
        } catch {
          setError("Stored lesson JSON could not be read");
          setLoading(false);
          return;
        }

        const reuseRes = await axios.post(
          `${API_URL}/api/ai-lessons/builder/create-from-existing`,
          {
            SourceLessonId: Number(sourceExistingLessonId),
            ClassID: Number(form.ClassID),
            AcademicYearLabel: form.AcademicYearLabel,
            TextbookName: form.TextbookName,
            ChapterTitle: form.ChapterTitle,
            ChapterNumber: form.ChapterNumber,
            LessonJson: parsedLessonJson,
            LanguageCode: form.LanguageCode,
            PublishMode: "DRAFT",
            GenerateAssignment: form.GenerateAssignment,
            PromptUsed: promptUsed || "",
            PromptID: selectedPrompt?.PromptID || null,
          },
          { headers: authHeaders }
        );

        const newLessonId = reuseRes.data?.lesson?.LessonId;
        if (!newLessonId) throw new Error("New lesson ID was not returned");

        const fullRes = await axios.get(`${API_URL}/api/ai-lessons/${newLessonId}`, {
          headers: authHeaders,
        });
        const lesson = fullRes.data?.lesson || reuseRes.data?.lesson || null;
        setGeneratedLesson(lesson);
        setUploadResult({ lesson });
        setLessonJsonText(JSON.stringify(lesson?.LessonJson || parsedLessonJson, null, 2));
        setSourceExistingLessonId("");
        setSelectedExistingLessonId("");
        setPreviewName("");
        setSelectedFileKind("");
        setSuccess(
          successMessage === "Lesson generated successfully"
            ? "New lesson created from the selected existing lesson"
            : successMessage
        );
        if (previewAfter) setTab(4);
        return lesson;
      }

      const fd = new FormData();
      fd.append("lessonFile", selectedFile);
      fd.append("LoginCampusCode", campusCode);
      fd.append("SubCampusCode", form.SubCampusCode);
      fd.append("DegreeCode", form.DegreeCode);
      fd.append("BranchCode", form.BranchCode);
      fd.append("Semester", form.Semester);
      fd.append("Section", form.Section);
      fd.append("ClassID", form.ClassID);
      fd.append("TextbookName", form.TextbookName);
      fd.append("ChapterTitle", form.ChapterTitle);
      fd.append("ChapterNumber", form.ChapterNumber);
      fd.append("AcademicYearLabel", form.AcademicYearLabel);
      fd.append("LanguageCode", form.LanguageCode);
      fd.append("ScanMetadata", String(form.ScanMetadata));
      fd.append("GenerateAssignment", String(form.GenerateAssignment));
      fd.append("SourceFileKind", selectedFileKind || "");
      fd.append("PromptUsed", [promptUsed, assessmentInstructions].filter(Boolean).join("\n\n"));
      fd.append("PromptID", selectedPrompt?.PromptID ? String(selectedPrompt.PromptID) : "");

      const res = await axios.post(`${API_URL}/api/ai-lessons/upload-and-generate`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const lesson = res.data?.lesson || null;
      setUploadResult(res.data);
      setGeneratedLesson(lesson);
      setLessonJsonText(
        JSON.stringify(
          typeof lesson?.LessonJson === "string" ? JSON.parse(lesson.LessonJson) : lesson?.LessonJson || {},
          null,
          2
        )
      );
      setSuccess(successMessage);
      if (previewAfter) setTab(4);
      return lesson;
    } catch (err) {
      setError(err.response?.data?.error || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromExisting = async () => {
    if (!selectedExistingLessonId) {
      setError("Please select an existing lesson");
      return;
    }
    if (!form.ClassID) {
      setError("Please select a class");
      return;
    }
    if (!form.AcademicYearLabel) {
      setError("Please select Academic Year");
      return;
    }

    let parsedLessonJson;
    try {
      parsedLessonJson = JSON.parse(lessonJsonText || "{}");
    } catch {
      setError("Lesson JSON is not valid JSON");
      return;
    }

    setReusing(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_URL}/api/ai-lessons/builder/create-from-existing`,
        {
          SourceLessonId: Number(selectedExistingLessonId),
          ClassID: Number(form.ClassID),
          AcademicYearLabel: form.AcademicYearLabel,
          TextbookName: form.TextbookName,
          ChapterTitle: form.ChapterTitle,
          ChapterNumber: form.ChapterNumber,
          LessonJson: parsedLessonJson,
          LanguageCode: form.LanguageCode,
          PublishMode: form.PublishMode,
          GenerateAssignment: form.GenerateAssignment,
        },
        { headers: authHeaders }
      );

      setSuccess(`Lesson created successfully. Lesson ID: ${res.data?.lesson?.LessonId || ""}`);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create lesson from existing lesson");
    } finally {
      setReusing(false);
    }
  };

  const handleSaveDraft = async () => {
    setError("");
    setSuccess("");

    const lessonId = uploadResult?.lesson?.LessonId || generatedLesson?.LessonId;

    if (!lessonId) {
      await handleGenerate({
        previewAfter: false,
        successMessage: "Lesson generated and saved as draft",
      });
      return;
    }

    if (String(generatedLesson?.ReviewStatus || "").toUpperCase() === "PUBLISHED") {
      setError("This lesson is already published. Create a new lesson if you want another draft.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ai-lessons/${lessonId}/save-draft`,
        { LoginCampusCode: campusCode },
        { headers: authHeaders }
      );

      setGeneratedLesson((prev) =>
        prev ? { ...prev, ReviewStatus: res.data?.ReviewStatus || "DRAFT" } : prev
      );
      setUploadResult((prev) =>
        prev?.lesson
          ? { ...prev, lesson: { ...prev.lesson, ReviewStatus: res.data?.ReviewStatus || "DRAFT" } }
          : prev
      );
      setSuccess("Lesson saved as draft");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save lesson as draft");
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewGeneratedLesson = () => {
    const lessonId = uploadResult?.lesson?.LessonId || generatedLesson?.LessonId;
    if (!lessonId || !generatedLesson) {
      setError("Generate or save the lesson as draft before previewing");
      return;
    }
    setError("");
    setTab(4);
  };

  const handleReloadLesson = async () => {
    const lessonId = uploadResult?.lesson?.LessonId || generatedLesson?.LessonId;
    if (!lessonId) return;

    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_URL}/api/ai-lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGeneratedLesson(res.data?.lesson || null);
      setLessonJsonText(JSON.stringify(res.data?.lesson?.LessonJson || {}, null, 2));
      setSuccess("Lesson refreshed");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to refresh lesson");
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    const lessonId = uploadResult?.lesson?.LessonId || generatedLesson?.LessonId;
    if (!lessonId) {
      setError("No generated lesson found to publish");
      return;
    }

    setPublishing(true);
    setError("");
    try {
      await axios.post(
        `${API_URL}/api/ai-lessons/${lessonId}/publish`,
        { LoginCampusCode: campusCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedLesson((prev) => prev ? { ...prev, ReviewStatus: "PUBLISHED" } : prev);
      setUploadResult((prev) =>
        prev?.lesson ? { ...prev, lesson: { ...prev.lesson, ReviewStatus: "PUBLISHED" } } : prev
      );
      setSuccess("Lesson published successfully");
    } catch (err) {
      setError(err.response?.data?.error || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };


  const handlePreviewExistingLesson = async (lessonId) => {
    setLoading(true);
    setError("");
    try {
      const row = await getExistingLessonDetail(lessonId);
      setGeneratedLesson({
        ...row,
        LessonJson:
          typeof row.LessonJson === "string"
            ? JSON.parse(row.LessonJson || "{}")
            : row.LessonJson || {},
      });
      setLessonJsonText(
        JSON.stringify(
          typeof row.LessonJson === "string"
            ? JSON.parse(row.LessonJson || "{}")
            : row.LessonJson || {},
          null,
          2
        )
      );
      setTab(4);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to preview lesson");
    } finally {
      setLoading(false);
    }
  };

  const handlePublishExistingLesson = async (lessonId) => {
    setPublishing(true);
    setError("");
    try {
      await axios.post(
        `${API_URL}/api/ai-lessons/${lessonId}/publish`,
        { LoginCampusCode: campusCode },
        { headers: authHeaders }
      );
      setSuccess("Lesson published successfully");
      await fetchExistingLessons({});
    } catch (err) {
      setError(err.response?.data?.error || "Failed to publish lesson");
    } finally {
      setPublishing(false);
    }
  };

  const handleToggleLessonSelection = (lessonId) => {
    setSelectedLessonIds((prev) =>
      prev.includes(Number(lessonId))
        ? prev.filter((id) => id !== Number(lessonId))
        : [...prev, Number(lessonId)]
    );
  };

  const selectableExistingLessonIds = useMemo(
    () =>
      (existingLessonsFiltered || [])
        .filter((row) => String(row.ReviewStatus || "").toUpperCase() !== "PUBLISHED")
        .map((row) => Number(row.LessonId))
        .filter(Boolean),
    [existingLessonsFiltered]
  );

  const allSelectableSelected =
    selectableExistingLessonIds.length > 0 &&
    selectableExistingLessonIds.every((id) => selectedLessonIds.includes(id));

  const handleToggleAllLessons = () => {
    setSelectedLessonIds((prev) => {
      if (allSelectableSelected) {
        return prev.filter((id) => !selectableExistingLessonIds.includes(id));
      }
      return Array.from(new Set([...prev, ...selectableExistingLessonIds]));
    });
  };

  const handleBulkPublish = async () => {
    if (!selectedLessonIds.length) {
      setError("Select at least one lesson to publish");
      return;
    }

    setBulkPublishing(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_URL}/api/ai-lessons/builder/existing-lessons/bulk-publish`,
        { LessonIds: selectedLessonIds, LoginCampusCode: campusCode },
        { headers: authHeaders }
      );
      setSuccess(`${res.data?.publishedCount || selectedLessonIds.length} lesson(s) published successfully`);
      setSelectedLessonIds([]);
      await fetchExistingLessons({});
    } catch (err) {
      setError(err.response?.data?.error || "Bulk publish failed");
    } finally {
      setBulkPublishing(false);
    }
  };

  const groupedLessons = useMemo(() => {
    const map = new Map();

    (existingLessonsFiltered || []).forEach((row) => {
      const key = groupKeyForLesson(row);
      if (!map.has(key)) {
        map.set(key, {
          key,
          SubjectCode: row.SubjectCode || "",
          SubjectName: row.SubjectName || "",
          rows: [],
        });
      }
      map.get(key).rows.push(row);
    });

    return Array.from(map.values()).sort((a, b) =>
      `${a.SubjectCode} ${a.SubjectName}`.localeCompare(`${b.SubjectCode} ${b.SubjectName}`)
    );
  }, [existingLessonsFiltered]);

  const flattenedVisibleRows = useMemo(() => {
    const out = [];

    groupedLessons.forEach((group) => {
      out.push({ type: "group", group });
      if (expandedSubjectGroups[group.key]) {
        group.rows.forEach((row) => out.push({ type: "lesson", group, row }));
      }
    });

    return out;
  }, [groupedLessons, expandedSubjectGroups]);

  const pageCount = Math.max(1, Math.ceil(flattenedVisibleRows.length / LESSONS_PER_PAGE));

  const pagedVisibleRows = useMemo(() => {
    const start = (lessonPage - 1) * LESSONS_PER_PAGE;
    return flattenedVisibleRows.slice(start, start + LESSONS_PER_PAGE);
  }, [flattenedVisibleRows, lessonPage]);


  const renderExistingLessonsPanel = (copyMode = false) => (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Paper elevation={0} sx={{ ...filterSectionSx }}>
          <Box sx={{ pl: 1 }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography sx={sectionTitleSx}>
                  {copyMode ? "Create From Existing Lesson" : "Existing Lessons"}
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: ".9rem" }}>
                  {copyMode
                    ? "Choose an existing lesson and use it as the starting point for a new lesson."
                    : "Review previously created lessons in your accessible scope."}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {!copyMode && (
                  <Button
                    variant="contained"
                    sx={primaryBtnSx}
                    startIcon={bulkPublishing ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                    onClick={handleBulkPublish}
                    disabled={bulkPublishing || selectedLessonIds.length === 0}
                  >
                    {bulkPublishing ? "Publishing..." : `Bulk Publish${selectedLessonIds.length ? ` (${selectedLessonIds.length})` : ""}`}
                  </Button>
                )}
                <Button variant="contained" sx={primaryBtnSx} startIcon={<FilterAltIcon />} onClick={handleApplyLessonFilters}>
                  Filter
                </Button>
                <Button variant="outlined" sx={outlinedBtnSx} startIcon={<ClearIcon />} onClick={handleClearLessonFilters}>
                  Clear
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(7, 1fr)" },
                gap: 1,
              }}
            >
              <Autocomplete
                multiple
                options={lessonFilterOptions.SubCampusCode}
                value={lessonFilters.SubCampusCode}
                onChange={(_, value) => setLessonFilters((p) => ({ ...p, SubCampusCode: value }))}
                disableCloseOnSelect
                filterSelectedOptions
                renderInput={(params) => <TextField {...params} label="SubCampus" size="small" sx={inputSx} />}
              />
              <Autocomplete
                multiple
                options={lessonFilterOptions.DegreeCode}
                value={lessonFilters.DegreeCode}
                onChange={(_, value) => setLessonFilters((p) => ({ ...p, DegreeCode: value }))}
                disableCloseOnSelect
                filterSelectedOptions
                renderInput={(params) => <TextField {...params} label="Degree" size="small" sx={inputSx} />}
              />
              <Autocomplete
                multiple
                options={lessonFilterOptions.BranchCode}
                value={lessonFilters.BranchCode}
                onChange={(_, value) => setLessonFilters((p) => ({ ...p, BranchCode: value }))}
                disableCloseOnSelect
                filterSelectedOptions
                renderInput={(params) => <TextField {...params} label="Branch" size="small" sx={inputSx} />}
              />
              <Autocomplete
                multiple
                options={lessonFilterOptions.Semester}
                value={lessonFilters.Semester}
                onChange={(_, value) => setLessonFilters((p) => ({ ...p, Semester: value }))}
                disableCloseOnSelect
                filterSelectedOptions
                renderInput={(params) => <TextField {...params} label="Semester" size="small" sx={inputSx} />}
              />
              <Autocomplete
                multiple
                options={lessonFilterOptions.Section}
                value={lessonFilters.Section}
                onChange={(_, value) => setLessonFilters((p) => ({ ...p, Section: value }))}
                disableCloseOnSelect
                filterSelectedOptions
                renderInput={(params) => <TextField {...params} label="Section" size="small" sx={inputSx} />}
              />
              <Autocomplete
                multiple
                options={lessonFilterOptions.SubjectCombo}
                value={lessonFilters.SubjectCombo}
                onChange={(_, value) => setLessonFilters((p) => ({ ...p, SubjectCombo: value }))}
                disableCloseOnSelect
                filterSelectedOptions
                renderInput={(params) => <TextField {...params} label="Subject Code + Subject Name" size="small" sx={inputSx} />}
              />
              <Autocomplete
                multiple
                options={lessonFilterOptions.Status}
                value={lessonFilters.Status}
                onChange={(_, value) => setLessonFilters((p) => ({ ...p, Status: value }))}
                disableCloseOnSelect
                filterSelectedOptions
                renderInput={(params) => <TextField {...params} label="Status" size="small" sx={inputSx} />}
              />
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={tableWrapSx}>
          {loading ? (
            <Box sx={{ p: 2 }}><Typography sx={{ color: "text.secondary" }}>Loading existing lessons...</Typography></Box>
          ) : !existingLessonsRaw.length ? (
            <Box sx={{ p: 2 }}><Typography sx={{ color: "text.secondary" }}>No existing lessons found in your scope.</Typography></Box>
          ) : (
            <>
              <Box sx={{ overflowX: "auto" }}>
                <TableContainer component="div">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        {!copyMode && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={allSelectableSelected}
                              indeterminate={
                                selectedLessonIds.length > 0 &&
                                !allSelectableSelected &&
                                selectableExistingLessonIds.some((id) => selectedLessonIds.includes(id))
                              }
                              onChange={handleToggleAllLessons}
                              sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" }, "&.MuiCheckbox-indeterminate": { color: "#fff" } }}
                            />
                          </TableCell>
                        )}
                        <TableCell style={{ width: 60 }}>#</TableCell>
                        <TableCell>SubCampus</TableCell>
                        <TableCell>Degree</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Semester</TableCell>
                        <TableCell>Section</TableCell>
                        <TableCell>SubjectCode</TableCell>
                        <TableCell>SubjectName</TableCell>
                        <TableCell>AcademicYear</TableCell>
                        <TableCell>Textbook</TableCell>
                        <TableCell>Chapter Title</TableCell>
                        <TableCell>Chapter Number</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedVisibleRows.map((item, idx) => {
                        if (item.type === "group") {
                          const { group } = item;
                          const isOpen = !!expandedSubjectGroups[group.key];
                          return (
                            <TableRow
                              key={`group-${group.key}`}
                              sx={{
                                backgroundColor: "rgba(25,118,210,0.10)",
                                cursor: "pointer",
                                "& td": { borderBottom: "1px solid rgba(25,118,210,0.15)", fontWeight: 900 },
                                "&:hover": { backgroundColor: "rgba(25,118,210,0.16)" },
                              }}
                              onClick={() => toggleSubjectGroup(group.key)}
                            >
                              {!copyMode && <TableCell padding="checkbox" />}
                              <TableCell>{isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}</TableCell>
                              <TableCell colSpan={13}>
                                {group.SubjectCode} - {group.SubjectName} ({group.rows.length} lesson{group.rows.length > 1 ? "s" : ""})
                              </TableCell>
                            </TableRow>
                          );
                        }

                        const row = item.row;
                        return (
                          <TableRow key={`lesson-${row.LessonId}`} sx={zebraRowSx(idx)}>
                            {!copyMode && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  size="small"
                                  checked={selectedLessonIds.includes(Number(row.LessonId))}
                                  disabled={String(row.ReviewStatus || "").toUpperCase() === "PUBLISHED"}
                                  onChange={() => handleToggleLessonSelection(row.LessonId)}
                                />
                              </TableCell>
                            )}
                            <TableCell>{(lessonPage - 1) * LESSONS_PER_PAGE + idx + 1}</TableCell>
                            <TableCell>{row.SubCampusCode || "-"}</TableCell>
                            <TableCell>{row.DegreeCode || "-"}</TableCell>
                            <TableCell>{row.BranchCode || "-"}</TableCell>
                            <TableCell>{row.Semester || "-"}</TableCell>
                            <TableCell>{row.Section || "-"}</TableCell>
                            <TableCell>{row.SubjectCode || "-"}</TableCell>
                            <TableCell>{row.SubjectName || "-"}</TableCell>
                            <TableCell>{row.AcademicYearLabel || "-"}</TableCell>
                            <TableCell>{row.TextbookName || "-"}</TableCell>
                            <TableCell>{row.ChapterTitle || "-"}</TableCell>
                            <TableCell>{row.ChapterNumber || "-"}</TableCell>
                            <TableCell><Chip size="small" label={row.ReviewStatus || "-"} /></TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={outlinedBtnSx}
                                  startIcon={<PreviewIcon />}
                                  onClick={() => handlePreviewExistingLesson(row.LessonId)}
                                >
                                  Preview
                                </Button>
                                {copyMode ? (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    sx={primaryBtnSx}
                                    startIcon={<ContentCopyIcon />}
                                    onClick={() => handleUseExistingLesson(row.LessonId)}
                                  >
                                    Use This
                                  </Button>
                                ) : String(row.ReviewStatus || "").toUpperCase() !== "PUBLISHED" ? (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    sx={primaryBtnSx}
                                    startIcon={<PublishIcon />}
                                    onClick={() => handlePublishExistingLesson(row.LessonId)}
                                    disabled={publishing}
                                  >
                                    Publish
                                  </Button>
                                ) : null}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  borderTop: "1px solid rgba(25,118,210,0.10)",
                  backgroundColor: "#fff",
                }}
              >
                <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
                  Showing {pagedVisibleRows.length} rows on this page
                </Typography>
                <Pagination
                  count={pageCount}
                  page={lessonPage}
                  onChange={(_, page) => setLessonPage(page)}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              AI Lesson Builder
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Select scoped class → choose a prompt → upload source → generate, preview, reuse, and publish lessons
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              sx={{
                ...outlinedBtnSx,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.70)",
                "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.10)" },
              }}
              startIcon={<RefreshIcon />}
              onClick={handleReloadLesson}
              disabled={loading || !generatedLesson?.LessonId}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={2500} onClose={() => setSuccess("")}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            </Snackbar>
          )}

          <Paper elevation={0} sx={{ ...surfaceSx, mb: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                px: 1,
                borderBottom: `1px solid ${BORDER}`,
                "& .MuiTab-root": { textTransform: "none", fontWeight: 800 },
                "& .Mui-selected": { color: THEME_ORANGE },
                "& .MuiTabs-indicator": { backgroundColor: THEME_ORANGE },
              }}
            >
              <Tab label="Create Lesson" icon={<DescriptionIcon />} iconPosition="start" />
              <Tab label="Prompts" icon={<AutoAwesomeIcon />} iconPosition="start" />
              <Tab label="Existing Lessons" icon={<HistoryIcon />} iconPosition="start" />
              <Tab label="Create From Existing" icon={<ContentCopyIcon />} iconPosition="start" />
              <Tab label="⚗ Immersive Preview" />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                      <Typography sx={sectionTitleSx}>Scoped Class Selection</Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={2}>
                          <TextField select label="Academic Year" name="AcademicYearLabel" value={form.AcademicYearLabel} onChange={handleChange} fullWidth size="small" sx={inputSx}>
                            {academicYearOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={5}>
                          <TextField select label="SubCampus" name="SubCampusCode" value={form.SubCampusCode} onChange={handleChange} fullWidth size="small" sx={inputSx}>
                            {subCampusOptions.map((opt) => (
                              <MenuItem key={opt.SubCampusCode} value={opt.SubCampusCode}>
                                {opt.SubCampusCode} {opt.CampusName ? `- ${opt.CampusName}` : ""}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={2}>
                          <TextField select label="Degree" name="DegreeCode" value={form.DegreeCode} onChange={handleChange} fullWidth size="small" sx={inputSx} disabled={!form.SubCampusCode}>
                            {degreeOptions.map((opt) => <MenuItem key={opt.DegreeCode} value={opt.DegreeCode}>{opt.DegreeCode}</MenuItem>)}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={2}>
                          <TextField select label="Branch" name="BranchCode" value={form.BranchCode} onChange={handleChange} fullWidth size="small" sx={inputSx} disabled={!form.DegreeCode}>
                            {branchOptions.map((opt) => <MenuItem key={opt.BranchCode} value={opt.BranchCode}>{opt.BranchCode}</MenuItem>)}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={1}>
                          <TextField select label="Semester" name="Semester" value={form.Semester} onChange={handleChange} fullWidth size="small" sx={inputSx} disabled={!form.BranchCode}>
                            {semesterOptions.map((opt) => <MenuItem key={opt.Semester} value={opt.Semester}>{opt.Semester}</MenuItem>)}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={2}>
                          <TextField select label="Section" name="Section" value={form.Section} onChange={handleChange} fullWidth size="small" sx={inputSx} disabled={!form.Semester}>
                            {sectionOptions.map((opt) => <MenuItem key={opt.Section} value={opt.Section}>{opt.Section}</MenuItem>)}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <TextField select label="Class" name="ClassID" value={form.ClassID} onChange={handleChange} fullWidth size="small" sx={inputSx} disabled={!form.Section}>
                            {classOptions.map((opt) => <MenuItem key={opt.ClassID} value={opt.ClassID}>{opt.ClassLabel}</MenuItem>)}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={2}>
                          <TextField label="SubjectCode" name="SubjectCode" value={form.SubjectCode} fullWidth size="small" sx={inputSx} InputProps={{ readOnly: true }} />
                        </Grid>

                        <Grid item xs={12} sm={2}>
                          <TextField label="SubjectName" name="SubjectName" value={form.SubjectName} fullWidth size="small" sx={inputSx} InputProps={{ readOnly: true }} />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ ...filterSectionSx }}>
                      <Box sx={{ pl: 1 }}>
                        <Typography sx={sectionTitleSx}>Lesson Builder</Typography>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ height: "100%", border: `1px dashed ${BORDER_HOVER}`, borderRadius: 2, p: 2, backgroundColor: "rgba(25,118,210,0.02)" }}>
                              <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>Upload Source</Typography>
                              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                                {sourceExistingLessonId ? "Existing Lesson Source" : "Textbook PDF / Photo / Lesson JSON"}
                              </Typography>
                              <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 1.5 }}>
                                {sourceExistingLessonId
                                  ? `Using existing Lesson #${sourceExistingLessonId}. Choose a file below to replace this source.`
                                  : acceptedTypesLabel}
                              </Typography>

                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button variant="contained" sx={primaryBtnSx} startIcon={<UploadIcon />} onClick={handleChooseFile}>
                                  Choose File
                                </Button>
                                <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.json,application/json" style={{ display: "none" }} onChange={handleFilePicked} />
                              </Stack>

                              {previewName ? (
                                <Box sx={{ mt: 2 }}>
                                  <Stack direction="row" spacing={1} flexWrap="wrap">
                                    <Chip icon={selectedFileKind === "JSON" ? <DataObjectIcon /> : <DescriptionIcon />} label={previewName} color="primary" variant="outlined" />
                                    {!!selectedFileKind && <Chip label={selectedFileKind} variant="outlined" sx={{ borderColor: "rgba(25,118,210,0.35)", color: "rgba(25,118,210,0.95)" }} />}
                                  </Stack>
                                </Box>
                              ) : (
                                <Typography sx={{ mt: 2, fontSize: "0.88rem", color: "text.secondary" }}>No file selected yet</Typography>
                              )}

                              {selectedFileKind === "JSON" && (
                                <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, backgroundColor: "rgba(25,118,210,0.03)", border: `1px solid ${BORDER}` }}>
                                  <Typography sx={{ fontWeight: 800, color: BLUE, mb: 0.5 }}>JSON Validation</Typography>
                                  <Typography sx={{ fontSize: "0.88rem", color: "text.secondary" }}>{jsonValidationMessage || "Checking JSON..."}</Typography>
                                </Box>
                              )}
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Box sx={{ height: "100%", borderRadius: 2, p: 2, backgroundColor: "rgba(25,118,210,0.02)", border: `1px solid ${BORDER}` }}>
                              <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>What this generates</Typography>
                              <Stack spacing={1}>
                                <Typography sx={{ fontSize: "0.93rem" }}>• Extracted chapter text from PDF/image</Typography>
                                <Typography sx={{ fontSize: "0.93rem" }}>• Or lesson/page content directly from uploaded JSON</Typography>
                                <Typography sx={{ fontSize: "0.93rem" }}>• Student-friendly lesson objectives</Typography>
                                <Typography sx={{ fontSize: "0.93rem" }}>• Interactive lesson sections</Typography>
                                <Typography sx={{ fontSize: "0.93rem" }}>• Quiz and assignment</Typography>
                                <Typography sx={{ fontSize: "0.93rem" }}>• Lesson mapped to selected scoped class and academic year</Typography>
                              </Stack>
                            </Box>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Typography sx={sectionTitleSx}>Review Quiz Settings</Typography>
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth size="small" type="number" label="Number of questions" value={quizQuestionCount} inputProps={{ min: 1, max: 100 }} onChange={(e) => setQuizQuestionCount(Math.max(1, Math.min(100, Number(e.target.value) || 1)))} sx={inputSx} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth select size="small" label="Question format" value={quizQuestionType} onChange={(e) => setQuizQuestionType(e.target.value)} sx={inputSx}>
                              <MenuItem value="MCQ">MCQ only</MenuItem>
                              <MenuItem value="THEORY">Theory only</MenuItem>
                              <MenuItem value="MIXED">MCQ + Theory</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid item xs={12} md={6}><Alert severity="info">Class is selected above. The backend controls grading, answer keys, and marks storage.</Alert></Grid>
                        </Grid>
                        <Typography sx={sectionTitleSx}>AI Lesson Prompt</Typography>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={3}>
                            <TextField fullWidth size="small" label="Prompt Category" value={LESSON_PROMPT_CATEGORY} sx={inputSx} InputProps={{ readOnly: true }} />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Autocomplete
                              options={prompts.filter((p) => !p.PromptCategory || p.PromptCategory === LESSON_PROMPT_CATEGORY)}
                              value={selectedPrompt}
                              getOptionLabel={(p) => p?.PromptTitle || ""}
                              isOptionEqualToValue={(a, b) => a?.PromptID === b?.PromptID}
                              onChange={(_, p) => {
                                setSelectedPrompt(p || null);
                                if (p) setPromptUsed(p.PromptText || "");
                              }}
                              renderInput={(params) => <TextField {...params} label="Prompt Bank" size="small" sx={inputSx} placeholder="Choose a saved lesson prompt" />}
                            />
                          </Grid>
                          <Grid item xs={12} md={5}>
                            <Box>
                              <Typography
                                sx={{
                                  mb: 0.5,
                                  fontSize: "0.75rem",
                                  color: "text.secondary",
                                  fontWeight: 700,
                                }}
                              >
                                Prompt used for this lesson
                              </Typography>

                              <Box
                                role="button"
                                tabIndex={0}
                                onClick={openPromptDialog}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openPromptDialog();
                                  }
                                }}
                                sx={{
                                  ...surfaceSx,
                                  px: 1.5,
                                  py: 1.15,
                                  minHeight: 42,
                                  cursor: "pointer",
                                  outline: "none",
                                  display: "flex",
                                  alignItems: "center",
                                  "&:hover": {
                                    borderColor: BORDER_HOVER,
                                    backgroundColor: "rgba(25,118,210,0.02)",
                                  },
                                  "&:focus-visible": {
                                    borderColor: BORDER_FOCUS,
                                    boxShadow: "0 0 0 2px rgba(240,120,40,0.12)",
                                  },
                                }}
                              >
                                <Typography
                                  noWrap
                                  sx={{
                                    width: "100%",
                                    fontSize: "0.9rem",
                                    lineHeight: 1.4,
                                    color: promptUsed ? "text.primary" : "text.secondary",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {promptUsed || "Click to view or edit the lesson prompt"}
                                </Typography>
                              </Box>

                              <Typography sx={{ mt: 0.5, fontSize: "0.75rem", color: "text.secondary" }}>
                                Click to open the full prompt.
                              </Typography>

                              <Dialog
                                open={promptDialogOpen}
                                onClose={closePromptDialog}
                                fullWidth
                                maxWidth="md"
                                PaperProps={{
                                  sx: {
                                    width: "min(900px, calc(100vw - 32px))",
                                    maxWidth: "900px",
                                    minHeight: { xs: "70vh", sm: 560 },
                                    maxHeight: "86vh",
                                    borderRadius: 3,
                                    border: `1px solid ${BORDER}`,
                                    boxShadow: 10,
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    p: { xs: 2, sm: 3 },
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                  }}
                                >
                                  <Typography sx={{ ...sectionTitleSx, mb: 1.5, fontSize: "1.15rem" }}>
                                    Full Lesson Prompt
                                  </Typography>

                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={18}
                                    maxRows={26}
                                    value={promptUsed}
                                    onChange={(e) => {
                                      setPromptUsed(e.target.value);
                                      setSelectedPrompt(null);
                                    }}
                                    sx={{
                                      ...inputSx,
                                      flex: 1,
                                      "& .MuiInputBase-root": {
                                        alignItems: "flex-start",
                                        fontSize: "0.95rem",
                                        lineHeight: 1.5,
                                      },
                                    }}
                                    placeholder="Enter the full lesson prompt"
                                  />

                                  <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                                    <Button variant="contained" sx={primaryBtnSx} onClick={closePromptDialog}>
                                      Done
                                    </Button>
                                  </Stack>
                                </Box>
                              </Dialog>
                            </Box>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Typography sx={sectionTitleSx}>Lesson Details</Typography>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField label="Textbook Name" name="TextbookName" value={form.TextbookName} onChange={handleChange} fullWidth size="small" sx={inputSx} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField label="Chapter Title" name="ChapterTitle" value={form.ChapterTitle} onChange={handleChange} fullWidth size="small" sx={inputSx} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField label="Chapter Number" name="ChapterNumber" value={form.ChapterNumber} onChange={handleChange} fullWidth size="small" sx={inputSx} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField select label="Language" name="LanguageCode" value={form.LanguageCode} onChange={handleChange} fullWidth size="small" sx={inputSx}>
                              <MenuItem value="en">English</MenuItem>
                              <MenuItem value="te">Telugu</MenuItem>
                              <MenuItem value="hi">Hindi</MenuItem>
                            </TextField>
                          </Grid>
                        </Grid>

                        {selectedFileKind === "JSON" && jsonPreview && (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Box>
                              <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>Source JSON Preview</Typography>
                              <Box sx={{ borderRadius: 2, p: 2, backgroundColor: "#fafafa", border: `1px solid ${BORDER}`, maxHeight: 220, overflow: "auto", fontFamily: "monospace", fontSize: "0.82rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {JSON.stringify(jsonPreview, null, 2)}
                              </Box>
                            </Box>
                          </>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} justifyContent="flex-end" flexWrap="wrap">
                          <Button
                            variant="contained"
                            sx={primaryBtnSx}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                            onClick={() => handleGenerate({ previewAfter: false })}
                            disabled={loading}
                          >
                            {loading ? "Generating..." : sourceExistingLessonId ? "Generate From Existing" : "Generate Lesson"}
                          </Button>

                          <Button variant="outlined" sx={outlinedBtnSx} startIcon={<DescriptionIcon />} onClick={handleSaveDraft} disabled={loading}>
                            Save as Draft
                          </Button>

                          <Button variant="outlined" sx={outlinedBtnSx} startIcon={<PreviewIcon />} onClick={handlePreviewGeneratedLesson} disabled={!generatedLesson?.LessonId}>
                            Preview
                          </Button>

                          <Button
                            variant="contained"
                            sx={primaryBtnSx}
                            startIcon={publishing ? <CircularProgress size={18} color="inherit" /> : <PublishIcon />}
                            onClick={handlePublish}
                            disabled={!generatedLesson?.LessonId || publishing}
                          >
                            {publishing ? "Publishing..." : "Publish"}
                          </Button>
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Paper elevation={0} sx={{ ...filterSectionSx }}>
                    <Box sx={{ pl: 1 }}>
                      <Typography sx={sectionTitleSx}>Common Prompt Bank — Lesson Builder</Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} md={3}>
                          <TextField select fullWidth size="small" label="SubCampus" value={promptForm.SubCampusCode || ""} onChange={(e) => setPromptForm((f) => ({ ...f, SubCampusCode: e.target.value || null }))} sx={inputSx}>
                            <MenuItem value="">All SubCampuses</MenuItem>
                            {subCampusOptions.map((opt) => <MenuItem key={opt.SubCampusCode} value={opt.SubCampusCode}>{opt.SubCampusCode}</MenuItem>)}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField fullWidth size="small" label="Category" value={promptForm.PromptCategory || LESSON_PROMPT_CATEGORY} onChange={(e) => setPromptForm((f) => ({ ...f, PromptCategory: e.target.value }))} sx={inputSx} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField fullWidth size="small" label="Prompt Title" value={promptForm.PromptTitle || ""} onChange={(e) => setPromptForm((f) => ({ ...f, PromptTitle: e.target.value }))} sx={inputSx} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth multiline minRows={5} label="Prompt" value={promptForm.PromptText || ""} onChange={(e) => setPromptForm((f) => ({ ...f, PromptText: e.target.value }))} sx={inputSx} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth multiline minRows={2} label="Purpose / Notes" value={promptForm.PromptPurpose || ""} onChange={(e) => setPromptForm((f) => ({ ...f, PromptPurpose: e.target.value }))} sx={inputSx} />
                        </Grid>
                        <Grid item xs={12}>
                          <Stack direction="row" spacing={1}>
                            <Button variant="contained" sx={primaryBtnSx} onClick={savePrompt} disabled={savingPrompt}>
                              {savingPrompt ? "Saving..." : promptForm.PromptID ? "Update Prompt" : "Save Prompt"}
                            </Button>
                            {promptForm.PromptID && <Button variant="outlined" sx={outlinedBtnSx} onClick={() => setPromptForm(EMPTY_PROMPT)}>Cancel Edit</Button>}
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>

                  {prompts.length === 0 ? (
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 3 }}><Typography color="text.secondary">No lesson prompts saved yet.</Typography></Paper>
                  ) : prompts.map((p) => (
                    <Paper key={p.PromptID} elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                            <Typography sx={{ fontWeight: 900, color: BLUE }}>{p.PromptTitle}</Typography>
                            <Chip size="small" label={p.PromptCategory || "LESSON_BUILDER"} />
                            {p.SubCampusCode && <Chip size="small" variant="outlined" label={p.SubCampusCode} />}
                          </Stack>
                          <Typography sx={{ whiteSpace: "pre-wrap", mt: 1 }}>{p.PromptText}</Typography>
                          {p.PromptPurpose && <Typography sx={{ color: "text.secondary", fontSize: ".88rem", mt: 1 }}>{p.PromptPurpose}</Typography>}
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Button variant="outlined" sx={outlinedBtnSx} onClick={() => setPromptForm({ ...EMPTY_PROMPT, ...p, PromptType: "LESSON" })}>Edit</Button>
                          <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => deletePrompt(p.PromptID)}>Delete</Button>
                          <Button variant="contained" sx={primaryBtnSx} onClick={() => { setSelectedPrompt(p); setPromptUsed(p.PromptText || ""); setTab(0); }}>Use Prompt</Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {tab === 2 && renderExistingLessonsPanel(false)}

            {tab === 3 && renderExistingLessonsPanel(true)}

            {tab === 4 && (
              <Box sx={{ p: 2 }}>
                {!generatedLesson ? (
                  <Paper elevation={0} sx={{ ...surfaceSx, p: 3 }}>
                    <Stack spacing={1} alignItems="center">
                      <Typography sx={{ fontSize: "2rem" }}>⚗️</Typography>
                      <Typography sx={{ fontWeight: 900 }}>No immersive preview yet</Typography>
                      <Typography sx={{ color: "text.secondary" }}>
                        Generate a lesson first. The AI will generate a standalone interactive HTML page using your selected prompt and uploaded document.
                      </Typography>
                    </Stack>
                  </Paper>
                ) : (
                  <Box>
                    <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2,
                      backgroundColor: "rgba(25,118,210,0.04)", border: `1px solid ${BORDER}`,
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
                      flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: ".875rem", color: "text.secondary" }}>
                        Interactive lesson preview (authenticated and sandboxed)
                      </Typography>
                      <Button variant="outlined" size="small" sx={outlinedBtnSx}
                        onClick={openLessonInNewTab} disabled={!lessonPreviewHtml || lessonPreviewLoading}>
                        Open in New Tab
                      </Button>
                    </Paper>
                    {lessonPreviewLoading && <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
                      <CircularProgress size={20} /><Typography>Loading interactive lesson…</Typography>
                    </Stack>}
                    {lessonPreviewError && <Alert severity="error" sx={{ mb: 2 }}>{lessonPreviewError}</Alert>}
                    {!lessonPreviewLoading && lessonPreviewHtml && (
                      <iframe
                        key={lessonPreviewId}
                        srcDoc={lessonPreviewHtml}
                        title="Immersive Lesson Preview"
                        referrerPolicy="no-referrer"
                        sandbox="allow-scripts"
                        style={{ width: "100%", height: "75vh", minHeight: 500,
                          border: "1px solid rgba(25,118,210,0.15)", borderRadius: 8, display: "block" }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            )}


          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default AiLessonBuilderPage;